import { gql, useMutation } from '@apollo/client';
import * as React from 'react';

import { logError } from '@/imports/logging/ClientLogger';

import { useCreateUpgradeRequest_createOrganizationUpgradeRequest } from './graphql-types/useCreateUpgradeRequest_createOrganizationUpgradeRequest';

const UPGRADE_TEXT_FRAGMENT = gql`
  fragment useCreateUpgradeRequest_upgradeTextFragment on CurrentTier_UpgradeText {
    title
    subtitle
  }
`;

const UPGRADE_REQUEST = gql`
  mutation useCreateUpgradeRequest_createOrganizationUpgradeRequest(
    $input: CreateOrganizationUpgradeRequestInput!
  ) {
    createOrganizationUpgradeRequest(input: $input) {
      result {
        _id
        sponsorship {
          currentTier {
            shouldShowUpgradeCTA

            # Update the cached upgrade texts after submitting an upgrade request
            postUpgradeText {
              ...useCreateUpgradeRequest_upgradeTextFragment
            }
            eventUpgradeText {
              ...useCreateUpgradeRequest_upgradeTextFragment
            }
            deletePostPaywallText {
              ...useCreateUpgradeRequest_upgradeTextFragment
            }
            discussionPaywallText {
              ...useCreateUpgradeRequest_upgradeTextFragment
            }
            leadsPaywallText {
              ...useCreateUpgradeRequest_upgradeTextFragment
            }
            messagesPaywallText {
              ...useCreateUpgradeRequest_upgradeTextFragment
            }
            followersPaywallText {
              ...useCreateUpgradeRequest_upgradeTextFragment
            }
          }
        }

        currentUserOrganizationPermission {
          canSubmitOrganizationSponsorshipTierUpgradeRequest
        }
      }
    }
  }
  ${UPGRADE_TEXT_FRAGMENT}
`;

export const useCreateUpgradeRequest = (
  organizationId: string
): {
  requestOrganizationUpgrade: (source: string) => Promise<void>;
  loading: boolean;
  submittedUpgrade: boolean;
} => {
  const [submittedUpgrade, setSubmittedUpgrade] = React.useState(false);

  const [requestUpgrade, { loading }] =
    useMutation<useCreateUpgradeRequest_createOrganizationUpgradeRequest>(
      UPGRADE_REQUEST
    );

  const requestOrganizationUpgrade = async (source: string): Promise<void> => {
    try {
      await requestUpgrade({
        variables: {
          input: {
            organizationId,
            source,
          },
        },
      });
      setSubmittedUpgrade(true);
    } catch (error) {
      logError(error);
    }
  };

  return {
    requestOrganizationUpgrade,
    loading,
    submittedUpgrade,
  };
};
